import { useState, useEffect } from "react";

/**
 * A simple hook to detect if the viewport matches a CSS media query.
 * @param query - The media query string to match (e.g., '(max-width: 768px)').
 * @returns A boolean indicating whether the query matches.
 */
export const useMediaQuery = (query: string): boolean => {
  // Use a function with window.matchMedia to safely get the initial state,
  // preventing errors during server-side rendering or in environments where `window` is not defined.
  const [matches, setMatches] = useState(() => {
    if (typeof window !== "undefined") {
      return window.matchMedia(query).matches;
    }
    return false; // Default value for SSR
  });

  useEffect(() => {
    // Again, guard against non-browser environments.
    if (typeof window === "undefined") {
      return;
    }

    const mediaQueryList = window.matchMedia(query);
    const listener = (event: MediaQueryListEvent) => setMatches(event.matches);

    // Modern way to add a listener. `addListener` is deprecated.
    mediaQueryList.addEventListener("change", listener);

    // Cleanup function to remove the listener when the component unmounts.
    return () => mediaQueryList.removeEventListener("change", listener);
  }, [query]); // Re-run the effect only if the query string changes.

  return matches;
};
